<!-- eslint-disable vue/no-v-html -->
<template>
  <teleport to="body">
    <div
      :id="uuid"
      ref="modalEl"
      class="hidden hs-overlay w-full h-full fixed top-0 left-0 z-[99] overflow-x-hidden overflow-y-auto"
      :data-hs-overlay-keyboard="!submiting"
      :class="[closeOnBackdropClick ? '' : 'static-backdrop']"
    >
      <div
        class="hs-overlay-open:my-7 hs-overlay-open:opacity-100 hs-overlay-open:duration-500 mt-0 opacity-0 ease-out transition-all sm:max-w-4xl mx-auto flex items-center"
        :class="[modalClasses, getDefaultModalClasses()]"
      >
        <div
          class="w-full flex flex-col bg-white shadow-default rounded-[6px] relative"
          :class="{ 'h-full': fillOnWidth >= windowWidth }"
        >
          <div :class="[modalContentClasses, getDefaultModalContentClasses()]">
            <slot></slot>
          </div>
          <div
            v-if="!hideFooter"
            class="flex justify-start items-center flex-wrap gap-[14px] p-[0px_56px_67px]"
            :class="modalFooterClasses"
          >
            <GenericBtn
              v-if="!hideSubmitBtn"
              class="uppercase"
              :text="submitText || $t('save')"
              :loading="submiting"
              :disabled="disabled"
              @click="handleSubmit"
            />
            <GenericBtn
              v-if="cancelText"
              class="uppercase"
              :text="cancelText"
              outlined-light
              :disabled="submiting"
              @click="handleClose"
            />
          </div>
        </div>
      </div>
      <InlineSvg
        v-if="!hideCloseBtn"
        src="/assets/svg/icons/close-white.svg"
        class="fixed top-[30px] right-[30px] transition-all cursor-pointer"
        :class="getDefaultIconClasses()"
        @click="handleClose"
      />
    </div>
  </teleport>
</template>

<script setup>
import _ from 'lodash';
import {
  nextTick,
  onBeforeUnmount,
  onDeactivated,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue';

const emit = defineEmits(['update:modelValue', 'submit']);
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
  submiting: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  closeOnBackdropClick: {
    type: Boolean,
    default: false,
  },
  hideCloseBtn: {
    type: Boolean,
    default: false,
  },
  hideFooter: {
    type: Boolean,
    default: false,
  },
  hideSubmitBtn: {
    type: Boolean,
    default: false,
  },
  submitText: {
    type: String,
    default: '',
  },
  cancelText: {
    type: String,
    default: '',
  },
  modalClasses: {
    type: String,
    default: '',
  },
  modalContentClasses: {
    type: String,
    default: '',
  },
  modalFooterClasses: {
    type: String,
    default: '',
  },
  fillOn: {
    type: Object,
    default: () => ({ size: '384', heightFull: false }),
  },
});

const uuid = _.uniqueId('modal');
const modalEl = ref(null);
watch(
  () => props.modelValue,
  (newVal) => {
    setTimeout(() => {
      if (newVal) {
        HSOverlay.open(modalEl.value);
      } else {
        handleClose();
      }
    });
  },
  { immediate: true }
);
const handleClose = () => {
  if (props.submiting) return;
  HSOverlay.close(modalEl.value);
  const overlays = document.querySelectorAll(
    '[data-hs-overlay-backdrop-template]'
  );
  for (const o of overlays) {
    o.remove();
  }
  document.body.style.removeProperty('overflow');
};

const handleSubmit = () => {
  handleClose();
  emit('submit');
};

import { useHelpers } from '@/composables/useHelpers';
import { auto } from '@popperjs/core';
const { getValInObj } = useHelpers();
const getDefaultModalClasses = () => {
  let classes = '';

  if (windowWidth.value < fillOnWidth.value) {
    classes += '!w-full absolute bottom-0 left-0 !m-0';

    if (getValInObj(props.fillOn, 'heightFull')) classes += ' h-full';
  }

  return classes;
};

const getDefaultModalContentClasses = () => {
  let classes = 'p-[57px_56px] overflow-y-auto';

  return classes;
};

const getDefaultIconClasses = () => {
  let classes = '';
  if (windowWidth.value < 550 || windowWidth.value < fillOnWidth.value)
    classes += 'hidden';
  return classes;
};

const windowWidth = ref(window.innerWidth);
const fillOnWidth = ref(0);

const handleResize = () => {
  setTimeout(() => {
    windowWidth.value = window.innerWidth;

    if (getValInObj(props.fillOn, 'size') == 'sm') {
      fillOnWidth.value = 640;
    } else if (getValInObj(props.fillOn, 'size') == 'md') {
      fillOnWidth.value = 768;
    } else if (getValInObj(props.fillOn, 'size') == 'lg') {
      fillOnWidth.value = 1024;
    } else if (!isNaN(getValInObj(props.fillOn, 'size'))) {
      fillOnWidth.value = getValInObj(props.fillOn, 'size');
    }
  });
};

onMounted(() => {
  handleResize();

  window.addEventListener('resize', handleResize);
  nextTick(() => {
    if (modalEl.value) {
      modalEl.value.addEventListener('close.hs.overlay', () => {
        emit('update:modelValue', false);
      });
    }
  });
});

onUnmounted(() => {
  handleClose();
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped></style>
